"use client";

/**
 * `TextareaField` — multi-line counterpart to {@link TextField}.
 * Mirrors the same structural pattern: read the rhf instance from
 * context, register the input by `field.name`, render a Label + control
 * + optional helper/error inside `FieldShell`.
 *
 * Differences from TextField:
 * - Renders a `<Textarea>` primitive instead of `<Input>`.
 * - `rows` is a textarea-only HTML attribute; forwarded when set.
 *
 * See `text-field.tsx` for the rationale on the `condition` defence-in-
 * depth check, the conditional spread pattern under
 * `exactOptionalPropertyTypes`, and why `required` does not yet drive
 * the asterisk.
 */

import * as React from "react";
import { Textarea } from "@aio/platform-ui";
import { useFormContext } from "../form-context";
import type { TextareaFieldDef } from "../types";
import { FieldShell } from "./field-shell";
import { readErrorMessage } from "./read-error";
import { resolveFieldPath } from "./path";

export interface TextareaFieldProps {
  field: TextareaFieldDef;
  /** Path prefix from a parent {@link RepeaterField}. See `text-field.tsx`. */
  pathPrefix?: string;
}

export function TextareaField({
  field,
  pathPrefix,
}: TextareaFieldProps): React.ReactElement | null {
  const { form } = useFormContext();

  if (field.condition && !field.condition(form.getValues())) {
    return null;
  }

  const path = resolveFieldPath(field.name, pathPrefix);
  const errorMessage = readErrorMessage(form.formState.errors, path);

  return (
    <FieldShell
      htmlFor={path}
      label={field.label}
      {...(field.description !== undefined ? { description: field.description } : {})}
      {...(errorMessage !== undefined ? { error: errorMessage } : {})}
    >
      <Textarea
        id={path}
        aria-invalid={errorMessage !== undefined ? true : undefined}
        {...(field.disabled ? { disabled: true } : {})}
        {...(field.placeholder !== undefined
          ? { placeholder: field.placeholder }
          : {})}
        {...(field.rows !== undefined ? { rows: field.rows } : {})}
        {...(field.maxLength !== undefined
          ? { maxLength: field.maxLength }
          : {})}
        {...form.register(path)}
      />
    </FieldShell>
  );
}
